import { createContext, useContext, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../hooks/use-toast";
import {
  loginSessionAvailable,
  logout,
  tokenExpired,
} from "../features/auth/authSlice";
import { useIntl } from "react-intl";

const AppContext = createContext(undefined);

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}

export function AppProvider({ children }) {
  const intl = useIntl();
  const [user, setUser] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // NEW: Show loading while checking
  const { toast } = useToast();
  const dispatch = useDispatch();

  // Get auth state from Redux
  const {
    isAuthenticated: reduxIsAuthenticated,
    token: reduxToken,
    user: reduxUser,
    tokenExpired: reduxTokenExpired,
  } = useSelector((state) => state.auth);

  // Initialize auth state on app start - WITH LOADING
  useEffect(() => {
    const initializeAuth = async () => {
      setIsCheckingAuth(true); // Show loading

      // Add small delay to ensure loading screen shows
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Check authentication
      dispatch(loginSessionAvailable());

      // Mark as initialized
      setIsInitialized(true);
      setIsCheckingAuth(false); // Hide loading
    };

    if (!isInitialized) {
      initializeAuth();
    }
  }, [dispatch, isInitialized]);

  // Sync with Redux auth state
  useEffect(() => {
    if (reduxIsAuthenticated && reduxToken && reduxUser) {
      try {
        const tokenValue = JSON.parse(reduxToken);
        const userData = JSON.parse(reduxUser);
        setSessionToken(tokenValue);
        setUser(userData);
      } catch (error) {
        console.error("Failed to parse auth data:", error);
        dispatch(logout());
      }
    } else {
      setSessionToken(null);
      setUser(null);
    }
  }, [reduxIsAuthenticated, reduxToken, reduxUser, dispatch]);

  // Handle token expiration
  useEffect(() => {
    if (reduxTokenExpired) {
      handleLogout("Your session has expired. Please log in again.");
    }
  }, [reduxTokenExpired]);

  const login = (token, userData) => {
    setSessionToken(token);
    setUser(userData);
    toast({
      title: intl.formatMessage({
        id: "Login.WelcomeBack",
        defaultMessage: "Welcome back!",
      }),
      description: intl.formatMessage(
        {
          id: "Login.WelcomeBackDesc",
          defaultMessage: `Hello {name}`,
        },
        {
          name: userData.displayname || userData.name,
        }
      ),
    });
  };

  const handleLogout = (message = "You have been logged out") => {
    dispatch(logout());
    setSessionToken(null);
    setUser(null);
    toast({
      title: "Logged out",
      description: message,
      variant: "destructive",
    });
  };

  const updateUser = (userData) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  // Check if user is authenticated and initialized
  const isAuthenticated =
    isInitialized && reduxIsAuthenticated && !!(sessionToken && user);

  return (
    <AppContext.Provider
      value={{
        user,
        sessionToken,
        isAuthenticated,
        isInitialized,
        isCheckingAuth, // NEW: Expose loading state
        login,
        logout: handleLogout,
        updateUser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
